import { LOCATION_CHANGED, type LocationChangedAction, push, replace } from '@takeshape/redux-little-router';
import getLoginRedirect from 'app/common/utils/get-login-redirect';
import { getToken } from 'app/common/utils/get-token';
import get from 'lodash/fp/get';
import queryString from 'query-string';
import type { SagaIterator } from 'redux-saga';
import { all, call, fork, put, race, select, take, takeLatest } from 'redux-saga/effects';
import type { ActionType } from 'typesafe-actions';
import { getCurrentBranchPath } from '../branches/selectors';
import { INSUFFICIENT_PERMISSIONS, UNAUTHORIZED_REQUEST } from '../common/action-types';
import { missingEntitlement } from '../common/actions';
import { getAsyncSaga, getRoutineSaga, takeLatestRequested } from '../common/utils/saga';
import { takeLatestRoutine } from '../common/utils/take-latest-routine';
import { showMessage } from '../messages/actions';
import { showMessageSaga } from '../messages/saga';
import { getPermission } from '../permissions/selectors';
import { getResourceString } from '../permissions/util';
import { GET_PROJECT } from '../project/action-types';
import { getProjectState } from '../project/selectors';
import { FORGOT_PASSWORD, RESET_PASSWORD, UPDATE_USER } from './action-types';
import {
  changePassword,
  createUser,
  emailValidated,
  getUser,
  type LoginRequestedPayload,
  loginUser,
  logoutUser,
  permissionsUpdated,
  setCliLoginToken,
  setRespondToCliError,
  type UpdateUserFulfilledAction
} from './actions';
import {
  getUser as apiGetUser,
  loginUser as apiLoginUser,
  logoutUser as apiLogoutUser,
  callCli,
  forgotPassword,
  registerUser,
  resetPassword,
  updateUser
} from './api';
import { getDomainState, getUserRequiresEmailValidation, selectLoggedIn } from './selectors';
import { persistAuthState } from './state';
import type { LoginUserArgs } from './types';
import { cliLoginServerSupported } from './util/cli-login-server-supported';

export const updateUserSaga = getAsyncSaga(UPDATE_USER, updateUser);

export function* updateUserFulfilledSaga(action: UpdateUserFulfilledAction) {
  const { meta } = action;
  if (!meta.silent) {
    yield put(showMessage('info', 'User updated', 1500));
  }

  yield call(persistState);
}

export const updateUserRejectedSaga = showMessageSaga(
  'error',
  'Could not update user. Please reload this page and try again.'
);

export function* changePasswordRequestedSaga(user: { password: string; newPassword: string }) {
  yield call(updateUser, { payload: user });
}

export function* watchForgotPassword() {
  yield takeLatestRoutine(FORGOT_PASSWORD, forgotPassword);
}

export function* userStatus() {
  // TODO Update resetPassword to pass type check
  // @ts-expect-error
  yield takeLatestRoutine(RESET_PASSWORD, resetPassword);
}

export function* persistState(): SagaIterator {
  const state = yield select(getDomainState);
  persistAuthState(state);
}

export function* forwardToLogin() {
  yield put(push('/login'));
}

export function* handleCreateUser() {
  yield call(persistState);
  yield put(push(getLoginRedirect()));
}

export function* handleUnauthorizedRequest() {
  yield put(showMessage('warning', 'You do not have sufficient permissions to perform this action.', 5000));
}

export function* handleUnauthenticatedRequest() {
  yield put(showMessage('warning', 'Your session has expired please login again.', 5000));
  yield put(logoutUser.requested());
}

export function* locationChanged({ payload }: LocationChangedAction): SagaIterator {
  const { pathname } = payload;
  const userRequiresEmailValidation = yield select(getUserRequiresEmailValidation);

  if (userRequiresEmailValidation) {
    if (pathname !== '/validate-email' && pathname !== '/logout' && pathname !== '/login') {
      yield put(push('/validate-email'));
    }
  } else {
    if (pathname === '/') {
      const isLoggedIn = yield select(selectLoggedIn);
      yield put(replace(isLoggedIn ? '/projects' : '/login'));
    } else if (pathname === '/projects') {
      const isLoggedIn = yield select(selectLoggedIn);
      if (!isLoggedIn) {
        yield put(replace('/login'));
      }
    } else if (pathname?.startsWith('/project/') && !pathname.endsWith('/delinquent')) {
      yield call(checkDelinquentLockout);
    }
  }
}

export function* checkDelinquentLockout(): SagaIterator {
  const project = yield select(getProjectState);
  const branchPath = yield select(getCurrentBranchPath);
  const isUnpaid = ['canceled', 'unpaid'].includes(project.getIn(['data', 'status', 'billing', 'subscriptionStatus']));
  if (isUnpaid) {
    const hasBillingPermissions = yield select(getPermission, {
      action: 'project-billing:update',
      resource: getResourceString('project-billing')
    });
    if (!hasBillingPermissions) {
      yield put(replace(`${branchPath}/delinquent`));
    }
  }
}

export function* recaptchaAndRegister({
  user,
  inviteCode,
  executeRecaptcha
}: {
  user: any;
  inviteCode?: string;
  executeRecaptcha: () => void;
}): SagaIterator {
  const ops = { ...user, inviteCode };

  // Invoke Recaptcha unless the URL has a registration token (only test automation would have this)
  const { registrationToken } = queryString.parse(window.location.search);
  if (registrationToken === undefined) {
    ops.recaptchaToken = yield call(executeRecaptcha);
  } else {
    ops.registrationToken = registrationToken;
  }

  return yield call(registerUser, ops);
}

export function* recaptchaAndLogin(payload: LoginRequestedPayload, meta: LoginUserArgs): SagaIterator {
  const ops: any = { ...payload };
  const { executeRecaptcha } = meta;

  const { registrationToken } = queryString.parse(window.location.search);
  if (registrationToken === undefined) {
    ops.recaptchaToken = yield call(executeRecaptcha);
  } else {
    ops.registrationToken = registrationToken;
  }

  return yield call(apiLoginUser, ops);
}

const createUserSaga = getRoutineSaga(createUser, recaptchaAndRegister, {
  handleBasicErrors: false
});

export function* handleRedirect(action: ActionType<typeof loginUser.fulfilled>) {
  const redirect = action.meta?.redirectOnSuccess;
  if (redirect) {
    yield put(push(redirect));
  }
}

// If logging into the CLI, send the auth token over
export function* respondToCli() {
  const search = queryString.parse(window.location.search);
  if (search.returnToCli && search.cliPort) {
    if (cliLoginServerSupported()) {
      const port = search.cliPort as string;
      try {
        yield call(callCli, { port });
      } catch {
        yield put(setRespondToCliError());
      }
    } else {
      yield put(setCliLoginToken(getToken()));
    }
  }
}

function* permissionsUpdatedSaga() {
  yield put(getUser.requested());
}

export function* handleEmailValidated() {
  // Keep auth token fresh to maintain pusher connection
  yield put(getUser.requested());
  const { fulfilled, rejected } = yield race({
    fulfilled: take(getUser.FULFILLED),
    rejected: take(getUser.REJECTED)
  });

  if (fulfilled) {
    yield put(push('/projects'));
  } else if (rejected) {
    console.error('getUser rejected in handleEmailValidated');
  }
}

export default function* root() {
  yield all([
    takeLatest(getUser.requested, getRoutineSaga(getUser, apiGetUser)),
    takeLatest(getUser.fulfilled, persistState),
    takeLatest(getUser.fulfilled, checkDelinquentLockout),
    takeLatest(createUser.REQUESTED, createUserSaga),
    fork(watchForgotPassword),
    fork(userStatus),
    takeLatest(INSUFFICIENT_PERMISSIONS, handleUnauthorizedRequest),
    takeLatest(UNAUTHORIZED_REQUEST, handleUnauthenticatedRequest),
    takeLatest(missingEntitlement, showMessageSaga('error', get('payload.message'))),
    takeLatestRequested(UPDATE_USER, updateUserSaga),
    takeLatest(`${UPDATE_USER}_FULFILLED`, updateUserFulfilledSaga),
    takeLatest(`${UPDATE_USER}_REJECTED`, updateUserRejectedSaga),
    takeLatest(
      changePassword.requested,
      getRoutineSaga(changePassword, changePasswordRequestedSaga, {
        handleBasicErrors: false
      })
    ),
    takeLatest(changePassword.fulfilled, showMessageSaga('info', 'Password updated', 1500)),
    takeLatest(changePassword.rejected, showMessageSaga('error', get('payload.message'))),
    takeLatest(loginUser.REQUESTED, getRoutineSaga(loginUser, recaptchaAndLogin, { handleBasicErrors: false })),
    takeLatest(loginUser.FULFILLED, handleRedirect),
    takeLatest(loginUser.FULFILLED, persistState),
    takeLatest(loginUser.FULFILLED, respondToCli),
    takeLatest(createUser.FULFILLED, handleCreateUser),
    takeLatest(logoutUser.REQUESTED, getRoutineSaga(logoutUser, apiLogoutUser)),
    takeLatest(logoutUser.REQUESTED, forwardToLogin),
    takeLatest(logoutUser.FULFILLED, persistState),
    takeLatest(LOCATION_CHANGED, locationChanged),
    takeLatest(`${GET_PROJECT}_FULFILLED`, checkDelinquentLockout),
    takeLatest(permissionsUpdated, permissionsUpdatedSaga),
    takeLatest(emailValidated, handleEmailValidated)
  ]);
}
